import React from "react";

import { CircularProgress } from "@mui/material";

import { useRemovePdfOverflow } from "../hooks/useRemovePdfOverflow";

import { CenteredText, Container, Divider, TableColumnName, TableCell, DocTable, DocTableRow, JsonRow, TextRow } from "../components";

const STATIC_CONTENT = [
  ["1.", "ОБЪЕКТНАЯ МОДЕЛЬ СИСТЕМЫ", true],
  ["1.1", "Поля"],
  ["1.2", "Типы данных"],
  ["1.3", "Объекты системы"],
  ["1.4", "Показатели"],
  ["2", "ФОРМАТ JSON-LD ДЛЯ СТАТИСТИЧЕСКИХ ПОКАЗАТЕЛЕЙ", true],
  ["2.1", "Описание формата"],
  ["2.2", "Пример документа JSON-LD с метаданными и данными показателя"],
  ["2.3", "Корневая структура документа"],
  ["2.4", "Отдельные правила"],
  ["3", "СТРУКТУРА ПОКАЗАТЕЛЕЙ", true],
]

function indicatorTableTitle(indicator) {
  switch (indicator.type) {
    case "Series":
      return `Временной ряд ${indicator.code}`;
    case "Reference":
      return `Справочник ${indicator.code}`;
    case "ParentChildClassifier":
      return `Классификатор типа родитель-дитя ${indicator.code}`;
    default:
      return null;
  }
}

function tableTitle(indicator) {
  if (indicator.indicator) {
    return `Показатель ${indicator.indicator} «${indicator.label}» (${indicatorTableTitle(indicator)})`;
  }

  switch (indicator.type) {
    case "Series":
      return `Временной ряд «${indicator.label}» (${indicator.table})`;
    case "Reference":
      return `Справочник «${indicator.label}» (${indicator.table})`;
    case "ParentChildClassifier":
      return `Классификатор типа родитель-дитя «${indicator.label}» (${indicator.table})`;
    default:
      return null;
  }
}

function DocExportGenerator({ onLoadSuccess, api, indicators, preview }) {
  const pageContainer = React.useRef(null);

  const [loading, setLoading] = React.useState(true);
  const [registryError, setRegistryError] = React.useState(false);

  const [indicatorsData, setIndicatorsData] = React.useState([]);

  React.useEffect(() => {
    api
      .send("dwh/exporting", { indicators })
      .then((indicatorsResponse) => {
        if (!Array.isArray(indicatorsResponse)) {
          setRegistryError(indicatorsResponse?.message || true);
          return;
        }

        setIndicatorsData(indicatorsResponse);
        onLoadSuccess?.();
      })
      .finally(() => setLoading(false));
  }, [api, indicators, onLoadSuccess]);

  const removePdfOverflow = useRemovePdfOverflow(pageContainer);

  React.useEffect(() => {
    if (loading || registryError || preview) return;
    removePdfOverflow();
  }, [loading, registryError, preview, removePdfOverflow]);

  if (registryError)
    return (
      <Container>
        <CenteredText>Ошибка в запросе {typeof registryError === "string" && `(${registryError})`}</CenteredText>
      </Container>
    );

  if (loading)
    return (
      <Container>
        <CenteredText>
          <CircularProgress size={32} />
        </CenteredText>
      </Container>
    );

  return (
    <Container ref={pageContainer}>
      <div>
        <TextRow style={{ height: 600, textAlign: "center" }}>Общество с ограниченной ответственностью «Контур Компонентс»</TextRow>
        <div style={{ height: 860 }}>
          <TextRow style={{ textAlign: "center" }}>ФОРМАТ РАСПРОСТРАНЕНИЯ ДАННЫХ JSON-LD</TextRow>
          <Divider id="divider" medium />
          <TextRow style={{ textAlign: "center" }}>Аналитическая витрина ЦАП</TextRow>
        </div>
        <TextRow style={{ textAlign: "center" }}>Москва 2025</TextRow>
        <Divider id="divider" />
      </div>

      <div>
        <TextRow style={{ textAlign: "center" }}>СОДЕРЖАНИЕ</TextRow>
        <Divider id="divider" medium />
        {STATIC_CONTENT.map(([number, title, isBold]) => (
          <div key={number} id="title" style={{ display: "flex" }}>
            <TextRow style={{ width: 60, fontWeight: isBold ? 600 : 400 }}>{number}</TextRow>
            <TextRow style={{ flex: 1, fontWeight: isBold ? 600 : 400 }}>{title}</TextRow>
            <TextRow style={{ width: 60, textAlign: "right" }} id="title-page">0</TextRow>
          </div>
        ))}
        {indicatorsData.map((indicator, index) => (
          <div key={index} id="title" style={{ display: "flex" }}>
            <TextRow style={{ width: 60 }}>3.{index + 1}</TextRow>
            <TextRow style={{ flex: 1 }}>{tableTitle(indicator)}</TextRow>
            <TextRow style={{ width: 60, textAlign: "right" }} id="title-page">0</TextRow>
          </div>
        ))}
        <div id="page-break" />
      </div>

      <div>
        <div data-title="0" style={{ fontWeight: 600 }}>1. ОБЪЕКТНАЯ МОДЕЛЬ СИСТЕМЫ</div>
        <Divider id="divider" medium />
        <TextRow>
          Формат публикации данных базируется на стандартах связанных данных (Linked Data) и структурированных данных (Structured Data).
        </TextRow>
        <Divider id="divider" medium />
        <TextRow>
          Формат реализован в BI платформе Contour BI, на базе которой разрабатывается Аналитическая платформа ЦАП, и связан с ее понятийным
          аппаратом.
        </TextRow>
        <Divider id="divider" />
      </div>

      <div>
        <div data-title="1" style={{ fontWeight: 600 }}>1.1 Поля</div>
        <Divider id="divider" medium />
        <TextRow>Поле (field) – это физическая колонка таблицы или элемент массива. Имеет одно из следующих назначений:</TextRow>
        <Divider id="divider" medium />
        <TextRow>- Измерение (dimension) – поле, относительно которого выполняется агрегация данных.</TextRow>
        <TextRow>- Факт (measure) – поле, содержащее агрегируемое числовое значение.</TextRow>
        <TextRow>- Атрибут (attribute) – поле, содержащее дополнительную информацию.</TextRow>
        <Divider id="divider" medium />
        <TextRow>Поле имеет публикуемый пользовательский тип данных, и, скрытый от пользователей физический тип данных.</TextRow>
        <Divider id="divider" medium />
        <TextRow>
          Все объекты имеют публикуемый пользовательский код (code), независимый от физического имени объекта базы данных, такого как имя
          таблицы, имя колонки. Физическое имя объекта базы данных может публиковаться для справки.
        </TextRow>
        <Divider id="divider" />
      </div>

      <div>
        <div data-title="2" style={{ fontWeight: 600 }}>1.2 Типы данных</div>
        <Divider id="divider" medium />
        <TextRow>
          Прикладные типы данных не зависят от СУБД, система поддерживает несколько типов СУБД, которые имеют разные физические типы данных.
        </TextRow>
        <Divider id="divider" medium />
        <TextRow>Поддерживаются следующие прикладные типы данных полей:</TextRow>
        <Divider id="divider" medium />
        <DocTable>
          <DocTableRow>
            <TableColumnName>Код</TableColumnName>
            <TableColumnName>Тип данных</TableColumnName>
          </DocTableRow>
          {[
            ["string", "Строка"],
            ["text", "Текст"],
            ["date", "Дата"],
            ["datetime", "Дата и время"],
            ["float", "Вещественный"],
            ["int", "Целый"],
            ["bigint", "Длинный целый"],
            ["decimal", "Десятичный"],
            ["bool", "Логический"],
            ["image", "Картинка"],
            ["url", "URL"],
            ["file", "Файл"],
          ].map(([code, type], index) => (
            <DocTableRow key={index}>
              <TableCell>{code}</TableCell>
              <TableCell>{type}</TableCell>
            </DocTableRow>
          ))}
        </DocTable>
        <Divider id="divider" />
      </div>

      <div>
        <div data-title="3" style={{ fontWeight: 600 }}>1.3 Объекты системы</div>
        <Divider id="divider" medium />
        <TextRow>
          Справочник (Code List) – список кодов, содержит два главных поля – код и значение, при этом код уникален. Может содержать
          дополнительные атрибуты, например, примечание, картинку и т.д. На справочник ссылаются измерения.
        </TextRow>
        <Divider id="divider" medium />
        <TextRow>
          Классификатор (Classification) – иерархический список кодов, содержит три главных поля – код, код родителя и значение, при этом
          код уникален. Может содержать дополнительные атрибуты, например, примечание, картинку и т.д. На справочник ссылаются измерения.
        </TextRow>
        <Divider id="divider" medium />
        <TextRow>
          Временной ряд (Time Series) – набор данных (dataset), имеющий обязательное поле-измерение Период наблюдения (period), набор
          измерений, которые могут быть произвольными значениями или ссылками на справочники и классификаторы, и как минимум одно числовое
          поле-факт.
        </TextRow>
        <Divider id="divider" />
      </div>

      <div>
        <div data-title="4" style={{ fontWeight: 600 }}>1.4 Показатель</div>
        <Divider id="divider" medium />
        <TextRow>
          Показатель (indicator) – это производный объект. Он является записью реестра показателей (справочника), имеет уникальный код,
          наименование, и паспорт – набор атрибутов.
        </TextRow>
        <Divider id="divider" medium />
        <TextRow>
          Данные показателя хранятся во временном ряду. Набор измерений временного ряда соответствует разрезностям показателя.
        </TextRow>
        <Divider id="divider" medium />
        <TextRow>
          В общем случае поддерживается два способа хранения данных – один временной ряд для одного показателя или один временной ряд для
          множества показателей, имеющий сходный набор разрезностей. В Аналитической витрине ЦАП в настоящее время применяется первый способ
          – каждый показатель имеет свой временной ряд.
        </TextRow>
        <Divider id="divider" medium />
        <TextRow>
          Показатель может иметь одну или несколько периодичностей, у каждой периодичности может быть собственная единица измерения.
        </TextRow>
        <Divider id="divider" />
      </div>

      <div>
        <div data-title="5" style={{ fontWeight: 600 }}>2 ФОРМАТ JSON-LD ДЛЯ СТАТИСТИЧЕСКИХ ПОКАЗАТЕЛЕЙ</div>
        <Divider id="divider" />
      </div>

      <div>
        <div data-title="6" style={{ fontWeight: 600 }}>2.1 Описание формата</div>
        <Divider id="divider" medium />
        <TextRow>
          Формат базируется на стандарте JSON и концепции JSON-LD – семантически определенных атрибутов, описанных в реестре schema.org и
          известных пользователям концепции, с возможностью описания связанных наборов объектов.
        </TextRow>
        <Divider id="divider" medium />
        <TextRow>
          JSON-LD поддерживает расширяемое множество типов объектов, например, видео, книга и т.д. Для распространения данных показателей
          используется тип объекта dataset – набор данных.
        </TextRow>
        <Divider id="divider" />
      </div>

      <div>
        <div data-title="7" style={{ fontWeight: 600 }}>2.2 Пример документа JSON-LD с метаданными и данными показателя</div>
        <Divider id="divider" medium />
        <JsonRow>{`{`}</JsonRow>
        <JsonRow>{`  "metadata": {`}</JsonRow>
        <JsonRow>{`    "@context": "https://schema.org/",`}</JsonRow>
        <JsonRow>{`    "url": "https://dap.gov.ru",`}</JsonRow>
        <JsonRow>{`    "@type": "Dataset",`}</JsonRow>
        <JsonRow>{`    "topic": null,`}</JsonRow>
        <JsonRow>{`    "identifier": "30654",`}</JsonRow>
        <JsonRow>{`    "name": "Доля валовой добавленной стоимости туристской индустрии в валовом внутреннем продукте Российской Федерации",`}</JsonRow>
        <JsonRow>{`    "inLanguage": "ru",`}</JsonRow>
        <JsonRow>{`    "description": "",`}</JsonRow>
        <JsonRow>{`    "periodicity": "7",`}</JsonRow>
        <JsonRow>{`    "dateModified": "2025-05-07",`}</JsonRow>
        <JsonRow>{`    "datePublished": "2025-06-17T11:31:42.710Z",`}</JsonRow>
        <JsonRow>{`    "temporal_span_end": "2023-12-31",`}</JsonRow>
        <JsonRow>{`    "temporal_span_start": "2023-01-01",`}</JsonRow>
        <JsonRow>{`    "creator": {`}</JsonRow>
        <JsonRow>{`      "url": "https://rosstat.gov.ru/",`}</JsonRow>
        <JsonRow>{`      "name": "ФЕДЕРАЛЬНАЯ СЛУЖБА ГОСУДАРСТВЕННОЙ СТАТИСТИКИ",`}</JsonRow>
        <JsonRow>{`      "@type": "Organization",`}</JsonRow>
        <JsonRow>{`      "identifier": "7708234640",`}</JsonRow>
        <JsonRow>{`      "contactPoint": {`}</JsonRow>
        <JsonRow>{`        "@type": "ContactPoint",`}</JsonRow>
        <JsonRow>{`        "email": "StarostinaDS@rosstat.gov.ru",`}</JsonRow>
        <JsonRow>{`        "person": "Старостина Д.С.",`}</JsonRow>
        <JsonRow>{`        "telephone": "+7 (495) 568-00-42",`}</JsonRow>
        <JsonRow>{`        "contactType": "Official"`}</JsonRow>
        <JsonRow>{`      }`}</JsonRow>
        <JsonRow>{`    },`}</JsonRow>
        <JsonRow>{`    "publisher": {`}</JsonRow>
        <JsonRow>{`      "url": "https://rosstat.gov.ru/",`}</JsonRow>
        <JsonRow>{`      "name": "ФЕДЕРАЛЬНАЯ СЛУЖБА ГОСУДАРСТВЕННОЙ СТАТИСТИКИ",`}</JsonRow>
        <JsonRow>{`      "@type": "Organization",`}</JsonRow>
        <JsonRow>{`      "identifier": "7708234640",`}</JsonRow>
        <JsonRow>{`      "contactPoint": {`}</JsonRow>
        <JsonRow>{`        "@type": "ContactPoint",`}</JsonRow>
        <JsonRow>{`        "email": "StarostinaDS@rosstat.gov.ru",`}</JsonRow>
        <JsonRow>{`        "person": "Старостина Д.С.",`}</JsonRow>
        <JsonRow>{`        "telephone": "+7 (495) 568-00-42",`}</JsonRow>
        <JsonRow>{`        "contactType": "Official"`}</JsonRow>
        <JsonRow>{`      }`}</JsonRow>
        <JsonRow>{`    },`}</JsonRow>
        <JsonRow>{`    "hasPart": [`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "@type": "CodeList",`}</JsonRow>
        <JsonRow>{`        "name": "Периодичность сбора",`}</JsonRow>
        <JsonRow>{`        "identifier": "periodicity",`}</JsonRow>
        <JsonRow>{`        "datatype": "int"`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "@type": "CodeList",`}</JsonRow>
        <JsonRow>{`        "name": "Общероссийский классификатор единиц измерений (ОКЕИ)",`}</JsonRow>
        <JsonRow>{`        "identifier": "okei",`}</JsonRow>
        <JsonRow>{`        "datatype": "string"`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "@type": "CodeList",`}</JsonRow>
        <JsonRow>{`        "name": "Происхождение данных",`}</JsonRow>
        <JsonRow>{`        "identifier": "data_origin",`}</JsonRow>
        <JsonRow>{`        "datatype": "int"`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "@type": "CodeList",`}</JsonRow>
        <JsonRow>{`        "name": "Общероссийский классификатор объектов административно-территориального деления",`}</JsonRow>
        <JsonRow>{`        "identifier": "terson",`}</JsonRow>
        <JsonRow>{`        "datatype": "string"`}</JsonRow>
        <JsonRow>{`      }`}</JsonRow>
        <JsonRow>{`    ],`}</JsonRow>
        <JsonRow>{`    "attribute": [`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "name": "Периодичность сбора",`}</JsonRow>
        <JsonRow>{`        "@type": "CodeListReference",`}</JsonRow>
        <JsonRow>{`        "field": "periodicity",`}</JsonRow>
        <JsonRow>{`        "propertyID": "dimension",`}</JsonRow>
        <JsonRow>{`        "datatype": "int",`}</JsonRow>
        <JsonRow>{`        "mandatory": true,`}</JsonRow>
        <JsonRow>{`        "code_list": "periodicity"`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "name": "Единица измерения",`}</JsonRow>
        <JsonRow>{`        "@type": "CodeListReference",`}</JsonRow>
        <JsonRow>{`        "field": "unit",`}</JsonRow>
        <JsonRow>{`        "propertyID": "dimension",`}</JsonRow>
        <JsonRow>{`        "datatype": "string",`}</JsonRow>
        <JsonRow>{`        "mandatory": true,`}</JsonRow>
        <JsonRow>{`        "code_list": "okei"`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "name": "Происхождение данных",`}</JsonRow>
        <JsonRow>{`        "@type": "CodeListReference",`}</JsonRow>
        <JsonRow>{`        "field": "data_origin",`}</JsonRow>
        <JsonRow>{`        "propertyID": "attribute",`}</JsonRow>
        <JsonRow>{`        "datatype": "int",`}</JsonRow>
        <JsonRow>{`        "mandatory": true,`}</JsonRow>
        <JsonRow>{`        "code_list": "data_origin"`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "name": "Общероссийский классификатор объектов административно-территориального деления (ОКАТО)",`}</JsonRow>
        <JsonRow>{`        "@type": "CodeListReference",`}</JsonRow>
        <JsonRow>{`        "field": "territory",`}</JsonRow>
        <JsonRow>{`        "propertyID": "dimension",`}</JsonRow>
        <JsonRow>{`        "datatype": "string",`}</JsonRow>
        <JsonRow>{`        "mandatory": true,`}</JsonRow>
        <JsonRow>{`        "code_list": "terson"`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "name": "Значение",`}</JsonRow>
        <JsonRow>{`        "@type": "Value",`}</JsonRow>
        <JsonRow>{`        "field": "value",`}</JsonRow>
        <JsonRow>{`        "propertyID": "measure",`}</JsonRow>
        <JsonRow>{`        "datatype": "float",`}</JsonRow>
        <JsonRow>{`        "mandatory": false`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "name": "Признак последней утвержденной версии",`}</JsonRow>
        <JsonRow>{`        "@type": "Value",`}</JsonRow>
        <JsonRow>{`        "field": "is_last_viewpoint",`}</JsonRow>
        <JsonRow>{`        "propertyID": "attribute",`}</JsonRow>
        <JsonRow>{`        "datatype": "numeric",`}</JsonRow>
        <JsonRow>{`        "mandatory": false`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "name": "Дата начала действия версии",`}</JsonRow>
        <JsonRow>{`        "@type": "Value",`}</JsonRow>
        <JsonRow>{`        "field": "startdate",`}</JsonRow>
        <JsonRow>{`        "propertyID": "attribute",`}</JsonRow>
        <JsonRow>{`        "datatype": "datetime",`}</JsonRow>
        <JsonRow>{`        "mandatory": false`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "name": "Версия",`}</JsonRow>
        <JsonRow>{`        "@type": "Value",`}</JsonRow>
        <JsonRow>{`        "field": "version",`}</JsonRow>
        <JsonRow>{`        "propertyID": "attribute",`}</JsonRow>
        <JsonRow>{`        "datatype": "string",`}</JsonRow>
        <JsonRow>{`        "mandatory": false`}</JsonRow>
        <JsonRow>{`      },`}</JsonRow>
        <JsonRow>{`      {`}</JsonRow>
        <JsonRow>{`        "name": "Период",`}</JsonRow>
        <JsonRow>{`        "@type": "Value",`}</JsonRow>
        <JsonRow>{`        "field": "period",`}</JsonRow>
        <JsonRow>{`        "propertyID": "dimension",`}</JsonRow>
        <JsonRow>{`        "datatype": "date",`}</JsonRow>
        <JsonRow>{`        "mandatory": true`}</JsonRow>
        <JsonRow>{`      }`}</JsonRow>
        <JsonRow>{`    ]`}</JsonRow>
        <JsonRow>{`  },`}</JsonRow>
        <JsonRow>{`  "references": [`}</JsonRow>
        <JsonRow>{`    {`}</JsonRow>
        <JsonRow>{`      "@type": "CodeList",`}</JsonRow>
        <JsonRow>{`      "name": "Периодичность сбора",`}</JsonRow>
        <JsonRow>{`      "identifier": "periodicity",`}</JsonRow>
        <JsonRow>{`      "data": [`}</JsonRow>
        <JsonRow>{`        {`}</JsonRow>
        <JsonRow>{`          "code": 7,`}</JsonRow>
        <JsonRow>{`          "value": "Ежегодная",`}</JsonRow>
        <JsonRow>{`          "version": "1"`}</JsonRow>
        <JsonRow>{`        }`}</JsonRow>
        <JsonRow>{`      ]`}</JsonRow>
        <JsonRow>{`    },`}</JsonRow>
        <JsonRow>{`    {`}</JsonRow>
        <JsonRow>{`      "@type": "CodeList",`}</JsonRow>
        <JsonRow>{`      "name": "Общероссийский классификатор единиц измерений (ОКЕИ)",`}</JsonRow>
        <JsonRow>{`      "identifier": "okei",`}</JsonRow>
        <JsonRow>{`      "data": [`}</JsonRow>
        <JsonRow>{`        {`}</JsonRow>
        <JsonRow>{`          "code": "744",`}</JsonRow>
        <JsonRow>{`          "value": "Процент",`}</JsonRow>
        <JsonRow>{`          "version": 1,`}</JsonRow>
        <JsonRow>{`          "parent_code": null`}</JsonRow>
        <JsonRow>{`        }`}</JsonRow>
        <JsonRow>{`      ]`}</JsonRow>
        <JsonRow>{`    },`}</JsonRow>
        <JsonRow>{`    {`}</JsonRow>
        <JsonRow>{`      "@type": "CodeList",`}</JsonRow>
        <JsonRow>{`      "name": "Происхождение данных",`}</JsonRow>
        <JsonRow>{`      "identifier": "data_origin",`}</JsonRow>
        <JsonRow>{`      "data": [`}</JsonRow>
        <JsonRow>{`        {`}</JsonRow>
        <JsonRow>{`          "code": 1,`}</JsonRow>
        <JsonRow>{`          "value": "Первичные",`}</JsonRow>
        <JsonRow>{`          "version": "1"`}</JsonRow>
        <JsonRow>{`        }`}</JsonRow>
        <JsonRow>{`      ]`}</JsonRow>
        <JsonRow>{`    },`}</JsonRow>
        <JsonRow>{`    {`}</JsonRow>
        <JsonRow>{`      "@type": "CodeList",`}</JsonRow>
        <JsonRow>{`      "name": "Общероссийский классификатор объектов административно-территориального деления",`}</JsonRow>
        <JsonRow>{`      "identifier": "terson",`}</JsonRow>
        <JsonRow>{`      "data": [`}</JsonRow>
        <JsonRow>{`        {`}</JsonRow>
        <JsonRow>{`          "code": "643004.АГ",`}</JsonRow>
        <JsonRow>{`          "value": "Российская Федерация без учета новых субъектов (с 01.01.2023)",`}</JsonRow>
        <JsonRow>{`          "version": 1,`}</JsonRow>
        <JsonRow>{`          "parent_code": null`}</JsonRow>
        <JsonRow>{`        }`}</JsonRow>
        <JsonRow>{`      ]`}</JsonRow>
        <JsonRow>{`    }`}</JsonRow>
        <JsonRow>{`  ],`}</JsonRow>
        <JsonRow>{`  "data": [`}</JsonRow>
        <JsonRow>{`    {`}</JsonRow>
        <JsonRow>{`      "@type": "Dataset",`}</JsonRow>
        <JsonRow>{`      "identifier": "ts_ehd_9480517",`}</JsonRow>
        <JsonRow>{`      "data": [`}</JsonRow>
        <JsonRow>{`        {`}</JsonRow>
        <JsonRow>{`          "periodicity": 7,`}</JsonRow>
        <JsonRow>{`          "unit": "744",`}</JsonRow>
        <JsonRow>{`          "data_origin": 1,`}</JsonRow>
        <JsonRow>{`          "territory": "643004.АГ",`}</JsonRow>
        <JsonRow>{`          "value": 2.8,`}</JsonRow>
        <JsonRow>{`          "is_last_viewpoint": "1",`}</JsonRow>
        <JsonRow>{`          "startdate": null,`}</JsonRow>
        <JsonRow>{`          "version": "1",`}</JsonRow>
        <JsonRow>{`          "period": "2023-12-31"`}</JsonRow>
        <JsonRow>{`        }`}</JsonRow>
        <JsonRow>{`      ]`}</JsonRow>
        <JsonRow>{`    }`}</JsonRow>
        <JsonRow>{`  ]`}</JsonRow>
        <JsonRow>{`}`}</JsonRow>
        <Divider id="divider" />
      </div>

      <div>
        <div data-title="8" style={{ fontWeight: 600 }}>2.3 Корневая структура документа</div>
        <Divider id="divider" medium />
        <TextRow>Документ состоит из трех ключей верхнего уровня:</TextRow>
        <TextRow>- metadata: описывает показатель, его структуру и атрибуты набора данных;</TextRow>
        <TextRow>- references: содержит описание справочников, на которые ссылаются поля набора данных;</TextRow>
        <TextRow>- data: содержит данные показателя.</TextRow>
        <Divider id="divider" />
      </div>

      <div>
        <div style={{ fontWeight: 600 }}>2.3.1 Роли атрибутов</div>
        <Divider id="divider" medium />
        <DocTable>
          <DocTableRow>
            <TableColumnName>Роль</TableColumnName>
            <TableColumnName>Значение поля propertyID</TableColumnName>
            <TableColumnName>Описание</TableColumnName>
          </DocTableRow>
          {[
            ["Dimension", "dimension", "Разрез показателя"],
            ["Measure", "measure", "Числовое значение показателя"],
            ["Attribute", "attribute", "Дополнительная информация"],
          ].map(([code, type, description], index) => (
            <DocTableRow key={index}>
              <TableCell>{code}</TableCell>
              <TableCell>{type}</TableCell>
              <TableCell>{description}</TableCell>
            </DocTableRow>
          ))}
        </DocTable>
        <Divider id="divider" />
      </div>

      <div>
        <div style={{ fontWeight: 600 }}>2.3.2 Метаданные показателя – metadata</div>
        <Divider id="divider" />
      </div>

      <div>
        <div style={{ fontWeight: 600 }}>2.3.2.1 Общие сведения (паспорт показателя)</div>
        <Divider id="divider" medium />
        <DocTable>
          <DocTableRow>
            <TableColumnName>Ключ</TableColumnName>
            <TableColumnName>Значение</TableColumnName>
            <TableColumnName>Описание</TableColumnName>
          </DocTableRow>
          {[
            ["@context", "https//schema.org/", "Словарь семантических атрибутов, используемый в формате"],
            ["url", "https://dap.gov.ru", "Адрес размещения данных показателя, в будущем будет ссылка на объект каталога открытых данных"],
            ["@type", "Dataset", "Тип объекта схемы JSON-LD, используемый для распространения данных и метаданных показателя"],
            ["topic", "Например, Туризм", "Раздел статистики"],
            ["identifier", "Например, 30654", "Уникальный код показателя"],
            [
              "name",
              "Например, Доля валовой добавленной стоимости туристской индустрии в валовом внутреннем продукте Российской Федерации",
              "Наименование показателя",
            ],
            ["inLanguage", "Например, ru", "Язык набора данных"],
            ["description", "", "Тексовое описание показателя, по умолчанию пусто"],
            ["periodicity", "Например, 7 – ежегодный", "Периодичность показателя, массив"],
            ["dateModified", "Например, 2025.01.25", "Дата изменения данных"],
            ["datePublished", "Например, 2025-03-26T12:29:32.823623+03:00", "Дата публикации"],
            ["temporal_span_start", "Например, 2024.01.01", "Начало временного ряда"],
            ["temporal_span_end", "Например, 2024.12.31", "Конец временного ряда"],
            ["creator", "", "Создатель документа"],
            ["url", "https://rosstat.gov.ru/", "Адрес сайта"],
            ["name", "ФЕДЕРАЛЬНАЯ СЛУЖБА ГОСУДАРСТВЕННОЙ СТАТИСТИКИ", "Наименование"],
            ["@type", "organization", "Тип"],
            ["identifier", "7708234640", "Идентификатор – ИНН"],
            ["contactPoint", "", "Контактная информация"],
            ["@type", "ContactPoint", "Тип: контактная информация"],
            ["email", "StarostinaDS@rosstat.gov.ru", "Электронная почта"],
            ["person", "Старостина Д.С.", "ФИО"],
            ["telephone", "(495)568-00-42 (доб. 99704)", "Телефон"],
            ["contactType", "Official", "Тип контакта"],
            ["publisher", "", "Публикатор документа"],
          ].map(([code, type, description], index) => (
            <DocTableRow key={index}>
              <TableCell>{code}</TableCell>
              <TableCell>{type}</TableCell>
              <TableCell>{description}</TableCell>
            </DocTableRow>
          ))}
        </DocTable>
        <Divider id="divider" />
      </div>

      <div>
        <div style={{ fontWeight: 600 }}>2.3.2.2 Справочники, примененные в показателе – hasPart</div>
        <Divider id="divider" medium />
        <DocTable>
          <DocTableRow>
            <TableColumnName>hasPart</TableColumnName>
            <TableColumnName></TableColumnName>
            <TableColumnName>
              Описание справочников и классификаторов, примененных в показателе и связанных наборов данных (связанные наборы в показателях
              не применяются)
            </TableColumnName>
          </DocTableRow>
          {[
            ["name", "Например, Общероссийский классификатор единиц измерений (ОКЕИ)", "Наименование справочника"],
            ["@type", "Например, CodeList", "Тип CodeList – справочник"],
            ["identifier", "Например, okei", "Код справочника"],
            ["datatype", "Например, integer", "Тип данных кода записи справочника"],
          ].map(([code, type, description], index) => (
            <DocTableRow key={index}>
              <TableCell>{code}</TableCell>
              <TableCell>{type}</TableCell>
              <TableCell>{description}</TableCell>
            </DocTableRow>
          ))}
        </DocTable>
        <Divider id="divider" />
      </div>

      <div>
        <div style={{ fontWeight: 600 }}>2.3.2.3 Поля временного ряда – attribute</div>
        <Divider id="divider" medium />
        <DocTable>
          <DocTableRow>
            <TableColumnName>Ключ</TableColumnName>
            <TableColumnName>Значение</TableColumnName>
            <TableColumnName>Описание</TableColumnName>
          </DocTableRow>
          {[
            ["attribute", "", "Поля временного ряда показателя (кастомное расширение JSON-LD)"],
            ["name", "Например, Территория", "Наименование"],
            [
              "@type",
              "Например, CodeListReference",
              <div>
                <div>Тип атрибута:</div>
                <div>CodeListReference – ссылка на справочник</div>
                <div>Value – произвольное значение</div>
              </div>,
            ],
            ["field", "Например, territory", "Код поля временного ряда"],
            ["code_list", "Например, terson", "Код справочника, если атрибут ссылается на справочник"],
            [
              "propertyID",
              "Например, dimension",
              <div>
                <div>Тип поля:</div>
                <div>dimension – измерение</div>
                <div>attribute – атрибут</div>
                <div>measure – факт (число)</div>
              </div>,
            ],
            [
              "mandatory",
              "Например, true",
              <div>
                <div>Обязательность</div>
                <div>false – нет</div>
                <div>true – да</div>
                <div>В данных никогда не будет пустых значений, если признак обязательности = true</div>
              </div>,
            ],
            ["datatype", "Например, string", "Прикладной тип данных атрибута"],
          ].map(([code, type, description], index) => (
            <DocTableRow key={index}>
              <TableCell>{code}</TableCell>
              <TableCell>{type}</TableCell>
              <TableCell>{description}</TableCell>
            </DocTableRow>
          ))}
        </DocTable>
        <Divider id="divider" />
      </div>

      <div>
        <div style={{ fontWeight: 600 }}>2.3.3 Справочники – references</div>
        <TextRow>Строки справочников, примененных в показателе. Каждый справочник имеет:</TextRow>
        <Divider id="divider" medium />
        <TextRow>identifier – идентификатор;</TextRow>
        <TextRow>name – наименование;</TextRow>
        <TextRow>data – массив строк.</TextRow>
        <Divider id="divider" medium />
        <DocTable>
          <DocTableRow>
            <TableColumnName>Ключ</TableColumnName>
            <TableColumnName>Значение</TableColumnName>
            <TableColumnName>Описание</TableColumnName>
          </DocTableRow>
          {[
            ["@type", "CodeList", "Тип объекта"],
            ["identifier", "Например, terson", "Код справочника"],
            ["name", "Например, Территория", "Наименование справочника"],
            ["data", "", "Строки справочника"],
            ["code", "Например, 643004.АГ", "Код строки"],
            ["parent_code", "", "Код родительской строки для классификатора"],
            ["value", "Например, Российская Федерация без учета новых субъектов (с 01.01.2023)", "Значение строки"],
            ["version", "2", "Номер версии строки"],
            ["Другие атрибуты", "2", "Список атрибутов справочника может быть расширен другими атрибутами"],
          ].map(([code, type, description], index) => (
            <DocTableRow key={index}>
              <TableCell>{code}</TableCell>
              <TableCell>{type}</TableCell>
              <TableCell>{description}</TableCell>
            </DocTableRow>
          ))}
        </DocTable>
        <Divider id="divider" />
      </div>

      <div>
        <div style={{ fontWeight: 600 }}>2.3.4 Данные – data</div>
        <Divider id="divider" medium />
        <TextRow>Содержит наборы данных показателей (dataset). Каждый набор данных содержит поля, описанные в ключе attribute.</TextRow>
        <Divider id="divider" />
      </div>

      <div>
        <div style={{ fontWeight: 600 }}>2.3.4.1 Данные временного ряда</div>
        <Divider id="divider" medium />
        <DocTable>
          <DocTableRow>
            <TableColumnName>Ключ</TableColumnName>
            <TableColumnName>Значение</TableColumnName>
            <TableColumnName>Описание</TableColumnName>
          </DocTableRow>
          {[
            ["@type", "Dataset", "Тип объекта"],
            ["identifier", "Например, ts_ehd_9480517", "Код временного ряда"],
            ["period", "Например, 2014-12-31", "Период наблюдения"],
            ["unit", "Например, 744", "Единица измерения"],
            ["territory", "Например, 643004.АГ", "Код территории"],
            ["periodicity", "Например, 7 – год", "Периодичность"],
            ["value", "Например, 2.9", "Значение показателя"],
            ["version", "Например, 2", "Версия"],
            [
              "Is_last_viewpoint",
              "Например, 1",
              <div>
                <div>Признак последней версии:</div>
                <div>0 – нет</div>
                <div>1 – да</div>
              </div>,
            ],
            ["Другие атрибуты", "Значения", "Другие поля временного ряда, описанные в разделе attributes"],
          ].map(([code, type, description], index) => (
            <DocTableRow key={index}>
              <TableCell>{code}</TableCell>
              <TableCell>{type}</TableCell>
              <TableCell>{description}</TableCell>
            </DocTableRow>
          ))}
        </DocTable>
        <Divider id="divider" />
      </div>

      <div>
        <div data-title="9" style={{ fontWeight: 600 }}>2.4 Отдельные правила</div>
        <Divider id="divider" medium />
        <TextRow>
          1. В случае, если показатель имеет несколько периодичностей, каждая из них выгружается как отдельный набор данных со своим
          значением поля Периодичность (periodicity) в разделе Метаданные (metadata).
        </TextRow>
        <Divider id="divider" medium />
        <TextRow>
          2. Каждый набор данных имеет единственную единицу измерения, и ее код повторяется в каждой строке данных для преемственности с
          ЕМИСС.
        </TextRow>
        <Divider id="divider" medium />
        <TextRow>3. Поле «Единица измерения» показателя определено как «Измерение».</TextRow>
        <Divider id="divider" medium />
        <TextRow>
          4. В некоторых показателях кроме единицы измерения может иметься «единица количества», которая описывает для какого количества
          товара наблюдается значение показателя. В общем случае «единица количества» может быть разной для разных элементов определенного
          измерения, например, «Товар». Пример: Показатель «Цены», единица измерения Рубль для всех строк набора данных, поле ОКЕИ содержит
          «единицу количества» для каждого товара – килограмм, тонна, литр, упаковка, за которую для этого товара приведена цена в рублях. В
          таких показателях «единица количества» определена как «Атрибут», наименование и код такого измерения не фиксированы и могут быть
          разными в разных показателях. При этом единица измерения как и в остальных показателях определена как «Измерение».
        </TextRow>
        <Divider id="divider" />
        <div id="page-break" />
      </div>

      <div>
        <div data-title="10" style={{ fontWeight: 600 }}>3 СТРУКТУРА ПОКАЗАТЕЛЕЙ</div>
        <Divider id="divider" />
      </div>

      {indicatorsData.map((indicator, index) => {
        return (
          <div key={index}>
            <div data-title={index + 11} style={{ fontWeight: 600 }}>3.{index + 1} {tableTitle(indicator)}</div>
            <Divider id="divider" medium />
            <DocTable>
              <DocTableRow>
                <TableColumnName>№</TableColumnName>
                <TableColumnName>Код поля</TableColumnName>
                <TableColumnName>Название</TableColumnName>
                <TableColumnName>Тип данных</TableColumnName>
                <TableColumnName>Примечание</TableColumnName>
                <TableColumnName>Ссылается на</TableColumnName>
              </DocTableRow>
              {indicator.attr
                .filter((attribute) => attribute.is_public)
                .map((attribute, index) => (
                  <DocTableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{attribute.code}</TableCell>
                    <TableCell>{attribute.label}</TableCell>
                    <TableCell>{attribute.datatype}</TableCell>
                    <TableCell>{attribute.description}</TableCell>
                    <TableCell>
                      {attribute.ref_label && attribute.ref_code && `Справочник «${attribute.ref_label}» (${attribute.ref_code})`}
                    </TableCell>
                  </DocTableRow>
                ))}
            </DocTable>
            {indicatorsData.length - 1 !== index && <Divider id="divider" />}
          </div>
        );
      })}
    </Container>
  );
}

export default React.memo(DocExportGenerator);
